import React, { useEffect, useState } from "react";
import { X, Send, Sparkles, CheckCircle2, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * BroadcastEverywhereModal — Update #143.
 *
 * One-click Karaoke + WordPress + Socials publishing. Given a source
 * audio asset (`assetId` + `sourceLabel`), lets the user tick the
 * destinations, fire the plan, and watch progress in real time.
 *
 * Progress flow:
 *   queued → separating|transcribing → rendering → succeeded
 *   → (on succeeded) broadcast fan-out → done
 */
export default function BroadcastEverywhereModal({
  open,
  onClose,
  assetId,
  sourceLabel = "your track",
  vocalOnly = false,          // true when source is already isolated vocals
  testidPrefix = "broadcast-everywhere",
}) {
  const [preflight, setPreflight] = useState(null);
  const [socialsOn, setSocialsOn] = useState(true);
  const [wpOn, setWpOn] = useState(true);
  const [platforms, setPlatforms] = useState(new Set());
  const [caption, setCaption] = useState("");
  const [headline, setHeadline] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/production/broadcast-everywhere/preflight");
        setPreflight(r.data);
        setPlatforms(new Set(r.data?.socials?.connected || []));
        setWpOn(!!r.data?.wordpress?.connected);
      } catch (e) {
        toast.error("Couldn't check connections");
      }
    })();
    // seed sensible defaults
    setHeadline(`🎤 Lyric video — ${sourceLabel}`);
    setCaption(
      `🎤 Fresh karaoke edit from the studio: ${sourceLabel}\n\n` +
      `#Karaoke #Radio #BroadcastFlow`
    );
    setJob(null);
  }, [open, sourceLabel]);

  // Poll the karaoke job so we can render live status
  useEffect(() => {
    if (!job?.job_id) return;
    if (["succeeded", "failed"].includes(job.status) && job.broadcast_completed_at) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/production/karaoke/${job.job_id}`);
        if (cancelled) return;
        setJob((prev) => ({ ...prev, ...r.data }));
        // Keep polling until BOTH the karaoke succeeded AND the broadcast
        // fan-out completed (or a hard failure).
        if (r.data?.status === "failed") return;
        if (r.data?.broadcast_completed_at) return;
        setTimeout(tick, 3500);
      } catch { /* ignore transient network */ }
    };
    setTimeout(tick, 2500);
    return () => { cancelled = true; };
  }, [job?.job_id]);

  const togglePlatform = (p) => {
    setPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  const submit = async () => {
    if (!socialsOn && !wpOn) {
      toast.error("Pick at least one destination");
      return;
    }
    if (socialsOn && platforms.size === 0) {
      toast.error("Pick at least one social platform (or turn socials off)");
      return;
    }
    setBusy(true);
    try {
      const body = {
        asset_id: assetId,
        karaoke: {
          asset_id: assetId,
          style: "gradient",
          vocal_only: vocalOnly,
          source_label_override: sourceLabel,
        },
        socials: {
          enabled: socialsOn,
          platforms: Array.from(platforms),
          caption: caption.trim() || undefined,
        },
        wordpress: {
          enabled: wpOn,
          headline: headline.trim() || undefined,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          status: "publish",
        },
      };
      const r = await api.post("/production/broadcast-everywhere", body);
      setJob({ job_id: r.data.job_id, status: "queued", progress_step: "queued" });
      toast.success("Broadcast queued! Rendering karaoke, then publishing.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Broadcast failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const readyBadge = (ok, label) => (
    <span className="type-caption inline-flex items-center gap-1 rounded-full px-2 py-0.5"
          style={{ background: ok ? "rgba(62,207,142,0.14)" : "rgba(180,180,180,0.10)",
                   color: ok ? "#3ecf8e" : "#8b8b8b" }}>
      {ok ? <CheckCircle2 size={10} /> : <X size={10} />} {label}
    </span>
  );

  const stepLabel = ({
    queued:        "Queued…",
    separating:    "Splitting vocals…",
    transcribing:  "Transcribing with Whisper…",
    rendering:     "Rendering MP4…",
    succeeded:     "Karaoke ready — publishing…",
    failed:        "Failed",
  })[job?.status] || (job ? `${job.status}…` : "");

  const socialsResult = job?.broadcast_socials_result;
  const wpResult      = job?.broadcast_wp_result;
  const done          = !!job?.broadcast_completed_at;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
         onClick={onClose}
         data-testid={`${testidPrefix}-modal`}>
      <div className="w-full max-w-lg mx-4 rounded-2xl border border-white/10 bg-neutral-950 p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-400" />
            <h3 className="type-title-3">Broadcast Everywhere</h3>
          </div>
          <button onClick={onClose} data-testid={`${testidPrefix}-close`}
                  className="rounded-full h-7 w-7 flex items-center justify-center hover:bg-white/10">
            <X size={14} />
          </button>
        </div>

        <p className="type-caption text-neutral-400 mb-4">
          One click: render a lyric video, publish a WordPress post, and share to your connected socials.
        </p>

        {job ? (
          /* ── Progress view ─────────────────────────────── */
          <div className="space-y-3">
            <div className="flex items-center gap-2 type-body">
              {done ? <CheckCircle2 size={14} className="text-emerald-400" />
                    : (job.status === "failed"
                        ? <AlertCircle size={14} className="text-red-400" />
                        : <Loader2 size={14} className="animate-spin text-amber-400" />)}
              <span>{done ? "Broadcast complete" : stepLabel}</span>
            </div>
            {job?.video_url && (
              <video controls src={job.video_url}
                     className="w-full rounded-lg" style={{ maxHeight: 220, background: "#000" }} />
            )}
            {socialsResult && !socialsResult.skipped && (
              <div className="rounded-lg border border-white/10 p-3">
                <div className="type-caption text-neutral-400 mb-1">Socials</div>
                <div className="flex flex-wrap gap-1.5">
                  {(socialsResult.queued || []).map((q) =>
                    <span key={q.post_id} className="type-caption rounded-full px-2 py-0.5"
                          style={{ background: "rgba(62,207,142,0.14)", color: "#3ecf8e" }}>
                      ✓ {q.platform}
                    </span>
                  )}
                  {(socialsResult.skipped || []).map((sk, i) =>
                    <span key={i} className="type-caption rounded-full px-2 py-0.5"
                          style={{ background: "rgba(180,180,180,0.10)", color: "#8b8b8b" }}>
                      {sk.platform} · {sk.reason}
                    </span>
                  )}
                </div>
              </div>
            )}
            {wpResult && !wpResult.skipped && (
              <div className="rounded-lg border border-white/10 p-3">
                <div className="type-caption text-neutral-400 mb-1">WordPress</div>
                {wpResult.ok ? (
                  <a href={wpResult.post_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 type-caption text-emerald-400 hover:underline">
                    <CheckCircle2 size={11} /> Post #{wpResult.post_id} published
                    <ExternalLink size={10} />
                  </a>
                ) : (
                  <div className="type-caption text-red-400">
                    Failed: {wpResult.reason || "unknown"}
                  </div>
                )}
              </div>
            )}
            {job.status === "failed" && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 type-caption text-red-300">
                {job.error || "Unknown error"}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={onClose} data-testid={`${testidPrefix}-done`}
                      className="rounded-full px-4 py-1.5 type-caption bg-white/10 hover:bg-white/15">
                {done ? "Done" : "Close (keeps running)"}
              </button>
            </div>
          </div>
        ) : (
          /* ── Plan-builder view ─────────────────────────── */
          <div className="space-y-4">
            {/* WordPress toggle */}
            <div className="rounded-xl border border-white/10 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={wpOn} onChange={(e) => setWpOn(e.target.checked)}
                       disabled={!preflight?.wordpress?.connected}
                       data-testid={`${testidPrefix}-toggle-wp`} />
                <span className="type-body flex-1">Publish to WordPress</span>
                {readyBadge(preflight?.wordpress?.connected, preflight?.wordpress?.connected ? "Connected" : "Not connected")}
              </label>
              {wpOn && preflight?.wordpress?.connected && (
                <div className="mt-3 space-y-2">
                  <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)}
                         placeholder="Post headline"
                         className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-1.5 type-caption"
                         data-testid={`${testidPrefix}-wp-headline`} />
                  <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
                         placeholder="Tags (comma-separated)"
                         className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-1.5 type-caption"
                         data-testid={`${testidPrefix}-wp-tags`} />
                </div>
              )}
            </div>

            {/* Socials toggle + platform pills */}
            <div className="rounded-xl border border-white/10 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={socialsOn} onChange={(e) => setSocialsOn(e.target.checked)}
                       disabled={(preflight?.socials?.connected?.length || 0) === 0}
                       data-testid={`${testidPrefix}-toggle-socials`} />
                <span className="type-body flex-1">Share to socials</span>
                {readyBadge(
                  (preflight?.socials?.connected?.length || 0) > 0,
                  `${preflight?.socials?.connected?.length || 0} connected`,
                )}
              </label>
              {socialsOn && (
                <>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(preflight?.socials?.supported || []).map((p) => {
                      const connected = preflight?.socials?.connected?.includes(p);
                      const active = platforms.has(p);
                      return (
                        <button key={p} type="button"
                                onClick={() => connected && togglePlatform(p)}
                                disabled={!connected}
                                data-testid={`${testidPrefix}-platform-${p}`}
                                className="type-caption rounded-full px-2.5 py-1 border disabled:opacity-40"
                                style={{
                                  background: active ? "rgba(74,158,255,0.18)" : "transparent",
                                  borderColor: active ? "#4a9eff" : "rgba(255,255,255,0.15)",
                                  color: active ? "#4a9eff" : "#c8c8c8",
                                }}>
                          {p}
                        </button>
                      );
                    })}
                  </div>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
                            rows={3} placeholder="Caption for all socials"
                            className="mt-3 w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 type-caption"
                            data-testid={`${testidPrefix}-caption`} />
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-full px-3 py-1.5 type-caption bg-white/5 hover:bg-white/10"
                      data-testid={`${testidPrefix}-cancel`}>
                Cancel
              </button>
              <button onClick={submit} disabled={busy}
                      className="rounded-full px-4 py-1.5 type-caption inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "linear-gradient(90deg, #ff8a1f, #ff6b35)", color: "#fff" }}
                      data-testid={`${testidPrefix}-submit`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Broadcast now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
